import React, { useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert, Box } from '@mui/material';
import { parseIFrameEvent } from 'utilities/parseIFrameEvent';
import Styles from './PaymentIframe.module.scss';


export interface Props {
  orderId: number;
  onPaymentFailure: (code: string, message: string) => void;
  onTokenReceived: (token: string) => Promise<void>;
  payFormUrl: string;
  paymentError?: (string | null)[];
  paymentProcessing: boolean;
}

const PaymentIframe = ({
  orderId,
  payFormUrl,
  paymentError = [],
  paymentProcessing,
  onPaymentFailure,
  onTokenReceived,
}: Props): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [iframeKey, setIframeKey] = useState<number>(1);

  const timeoutRef = useRef<number>();
  const onPaymentFailureRef = useRef(onPaymentFailure);
  const onTokenReceivedRef = useRef(onTokenReceived);

  useEffect(() => {
    onPaymentFailureRef.current = onPaymentFailure;
    onTokenReceivedRef.current = onTokenReceived;
  }, [onPaymentFailure, onTokenReceived]);

  useEffect(() => {
    const handleIframeTask = (event: MessageEvent): void => {
      const {
        eventType,
        token,
        code,
        reason
      } = parseIFrameEvent(event, payFormUrl); 

      const timeDelay = (2 ** iframeKey) * 100;

      if (eventType==='success') {
        onTokenReceivedRef.current(token);
      } else if (eventType === 'failure') {
        // Let the caller know what the failure was. 
        onPaymentFailureRef.current(code, reason);

        // If an error occurred, this will redraw the iframe with the
        // payment gateway and re-register the event listeners.  
        // It will only respond to the first 5 error, then give up. 
        if(iframeKey < 6)
        {
          timeoutRef.current = window.setTimeout(() => {
            setIframeKey((prevIframeKey) => prevIframeKey + 1);
          }, timeDelay);
        }
      }      
    };
    
    // eslint-disable-next-line no-console
    console.log(`[PaymentIframe] Listening for messages from iframe with key ${iframeKey}`);
    window.addEventListener('message', handleIframeTask, false);

    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('message', handleIframeTask);
    };
  }, [payFormUrl, iframeKey]);

  return (
    <div>
      {!paymentProcessing &&
        paymentError &&
        paymentError.length > 0 &&
        paymentError.map(
          (message, index) =>
            message && (
              <Alert key={index} severity="error" sx={{ marginY: '1rem' }}>
                {message}
              </Alert>
            )
        )}
      <div className={Styles.wrapper}>
        {(loading || paymentProcessing) && (
          <Box
            role="status"
            sx={{
              margin: '4rem auto',
              textAlign: 'center',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            className="loading"
          >
            <CircularProgress data-testid="loading-spinner" color="info" />
          </Box>
        )}
        {payFormUrl && !paymentProcessing && (
          <iframe
            key={iframeKey}
            title={`Order #${orderId}`}
            data-testid="payment-iframe"
            referrerPolicy="origin"
            src={payFormUrl}
            className={Styles.pay_form_iframe}
            onLoad={() => setLoading(false)}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentIframe;
